import { add } from 'date-fns/add';
import { formatISO } from 'date-fns/formatISO';
import { isValid } from 'date-fns/isValid';
import { parseISO } from 'date-fns/parseISO';
const DEFAULT_OPTIONS = {
    ISO8601DateExtended: false,
};
/**
 * Starts with `sunday`
 */
export const daysOfWeek = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];
/**
 * Converts the values of a given `Date` to a date formatted string
 *
 * @param date the `Date` you want converted to a string
 * @param options the options for the conversion
 * @example
 * // If you want to convert only the date part of the Date
 * // you can use the ISO8601DateExtended option
 * // legacy example: moment(date.substring(0, 10))
 *
 * const date = new Date(); // '2021-01-01T00:00:00.000Z'
 * const options = { ISO8601DateExtended: true };
 * getStringFromDate(date, options); // returns: '2021-01-01'
 */
export const getStringFromDate = (date, options = DEFAULT_OPTIONS) => {
    const { ISO8601DateExtended } = options;
    /**
     *  DEFAULT Date-fns options
     *  @see https://date-fns.org/v4.1.0/docs/formatISO#arguments
     */
    return formatISO(date, {
        representation: ISO8601DateExtended ? 'date' : 'complete',
        format: 'extended',
    });
};
export const getDateOnlyStringFromDate = (date) => {
    return getStringFromDate(date, {
        ISO8601DateExtended: true,
    });
};
export function getDate(value) {
    return typeof value === 'string' ? parseISO(value) : value;
}
export function isCompanyStartOfWeek(date, weekStart) {
    // Company weekStart is 1-based, so we need to subtract 1 to get the 0-based day of the week
    return date.getDay() === weekStart - 1;
}
export function getCompanyStartOfWeek(date, weekStart) {
    // Company weekStart is 1-based, so we need to subtract 1 to get the 0-based day of the week
    return add(date, { days: weekStart - 1 - date.getDay() });
}
/**
 * Checks if a date is a weekend
 * @param date - The date to check if it is a weekend in `yyyy-MM-dd` format
 * @param weekendDays - The days of the week that are considered weekends
 * @returns true if the date is a weekend, false otherwise
 */
export function isWeekend(date, weekendDays) {
    const d = new Date(date);
    if (!isValid(d)) {
        throw new Error(`Invalid date: ${date.toString()}`);
    }
    const day = daysOfWeek[d.getUTCDay()];
    return weekendDays.includes(day);
}
/**
 * Calculates the number of leave days in a date range, excluding weekends and holidays.
 * Holidays that fall on weekends are not double-counted.
 *
 * @param startDate - The start date in `yyyy-MM-dd` format
 * @param endDate - The end date in `yyyy-MM-dd` format
 * @param weekendDays - The days of the week that are considered weekends
 * @param holidays - Array of holiday objects with `date` (yyyy-MM-dd) and `name`
 * @returns A breakdown of the leave days calculation
 */
export function calculateLeaveDays(startDate, endDate, weekendDays, holidays) {
    const start = new Date(`${startDate}T00:00:00Z`);
    const end = new Date(`${endDate}T00:00:00Z`);
    if (!isValid(start) || !isValid(end)) {
        return {
            calendarDays: 0,
            weekendDaysCount: 0,
            holidayDaysCount: 0,
            leaveDays: 0,
            weekendDates: [],
            holidayDates: [],
        };
    }
    const holidaySet = new Map();
    for (const h of holidays) {
        holidaySet.set(h.date, h.name);
    }
    const weekendDates = [];
    const holidayDates = [];
    let calendarDays = 0;
    const cur = new Date(start);
    while (cur <= end) {
        calendarDays++;
        const dateStr = cur.toISOString().slice(0, 10);
        const dayName = daysOfWeek[cur.getUTCDay()];
        const isWeekendDay = weekendDays.includes(dayName);
        const holidayName = holidaySet.get(dateStr);
        if (isWeekendDay) {
            weekendDates.push(dateStr);
        }
        else if (holidayName !== undefined) {
            // Only count as holiday if it's NOT already a weekend
            holidayDates.push({ date: dateStr, name: holidayName });
        }
        cur.setUTCDate(cur.getUTCDate() + 1);
    }
    const leaveDays = calendarDays - weekendDates.length - holidayDates.length;
    return {
        calendarDays,
        weekendDaysCount: weekendDates.length,
        holidayDaysCount: holidayDates.length,
        leaveDays: Math.max(0, leaveDays),
        weekendDates,
        holidayDates,
    };
}
//# sourceMappingURL=dates.js.map